import joi, { ObjectSchema } from 'joi';
import { get, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { Moment } from 'moment';
import {
    IResearchProposal,
    IResearchProposalStudyDesign,
    ITimeline,
    IDataRequest,
} from '@vivli/features/data-requests/infrastructure/interface';
import { JoiValidationOptionsConstant } from '@vivli/shared/infrastructure/constants';

const studyDesignSchema: ObjectSchema<IResearchProposalStudyDesign> = joi
    .object({
        briefDescription: joi.string().max(9000).required().label('Brief Description'),
        elementsforOutcomes: joi.string().max(9000).required().label('Outcomes Elements Description'),
        mainPredictorIndependentVariable: joi.string().max(5000).required().label('Main Predictor/Independent Variable Description'),
        otherVariablesOfInterest: joi.string().max(9000).label('Other Variables Description').allow('').optional(),
    })
    .options(JoiValidationOptionsConstant);

const getTimelineSchema = (isAdmin: boolean, researchProposal: IResearchProposal, earliestStartDate?: Date) => {
    //no min date validation for vivli admin
    if (isAdmin) {
        return joi.object({
            projectStartDate: joi.date().required().label('Project Start Date at least 5 Months After Request Start Date'),
            analysisCompletionDate: joi.date().required().label('Analysis Completion Date'),
        });
    } else {
        return joi.object({
            projectStartDate: joi
                .date()
                .required()
                .label('Project Start Date at least 5 Months After Request Start Date')
                .min(earliestStartDate)
                .message('Project Start Date must be at least 5 months after the Request Start Date.')
                .messages({
                    'date.min': 'Project Start Date must be at least 5 months after the Request Start Date.',
                }),
            analysisCompletionDate: joi
                .date()
                .required()
                .label('Analysis Completion Date')
                .custom((value, helpers) => {
                    // Access the value of the 'projectStartDate' field using helpers.state.ancestors[0]
                    const startDate = helpers.state.ancestors[0].projectStartDate;

                    // If start_date is not defined yet, skip validation.
                    if (!startDate) {
                        return value;
                    }

                    // Calculate the date 12 months after the start_date
                    const twelveMonthsLater = new Date(startDate);
                    twelveMonthsLater.setFullYear(twelveMonthsLater.getFullYear() + 1);

                    // Compare the end_date with the calculated date
                    if (value.getTime() < twelveMonthsLater.getTime()) {
                        return helpers.error('date.greaterThanTwelveMonths', {
                            start_date: startDate.toISOString().split('T')[0],
                        });
                    }

                    return value;
                }, 'Date must be at least 12 months from start date'),
        });
    }
};

export const getResearchProposalSchema = (isAdmin: boolean, researchProposal: IResearchProposal, requestStartDate: Date) => {
    const parsedDate = requestStartDate ? new Date(requestStartDate) : null;
    const earliestStartDate = parsedDate ? new Date(parsedDate.getFullYear(), parsedDate.getMonth() + 5, parsedDate.getDate()) : new Date();

    return joi
        .object({
            laySummary: joi.string().required().label('Lay Summary'),
            specificAimsObjectives: joi.string().required().label('Objective'),
            purposeOfAnalysis: joi.array().min(1).required().label('Purpose of Analysis').default([]),
            outcomes: joi.array().min(1).required().label('Outcome(s)').default([]),
            researchProposalStudyDesign: studyDesignSchema,
            projectTimeline: getTimelineSchema(isAdmin, researchProposal, earliestStartDate),
            disseminationAndPublicationPlan: joi.string().max(9000).label('Publication Plan'),
            purposeOfAnalysisOtherDescription: joi.when('purposeOfAnalysis', {
                is: joi.array().items(joi.string().valid('Other').required(), joi.any()).required(),
                then: joi.string().required().label('Other Purpose of Analysis'),
                otherwise: joi.optional(),
            }),
            outcomesOtherDescription: joi.when('outcomes', {
                is: joi.array().items(joi.string().valid('Other').required(), joi.any()).required(),
                then: joi.string().required().label('Other Outcome'),
                otherwise: joi.optional(),
            }),
        })
        .options(JoiValidationOptionsConstant);
};

//note: this is not used directly, schema function called by data request form hook
//kept it here because it was here before and for compatibility with other form hooks
export function useResearchProposalFormHook(
    researchProposal: IResearchProposal,
    isAdmin: boolean,
    requestStartDate?: Date,
    mode: 'onBlur' | 'onChange' | 'onTouched' | 'all' = 'all'
) {
    const parsedDate = requestStartDate ? new Date(requestStartDate) : null;
    const earliestStartDate = parsedDate ? new Date(parsedDate.getFullYear(), parsedDate.getMonth() + 5, parsedDate.getDate()) : new Date();
    const schema = getResearchProposalSchema(isAdmin, researchProposal, earliestStartDate);

    return useForm({
        mode,
        resolver: joiResolver(schema.options(JoiValidationOptionsConstant)),
        defaultValues: { researchProposal, isAdmin, requestStartDate },
        reValidateMode: 'onChange',
    });
}
